import { DotsIcon } from '@dynamika-shared/ui/icons';
import { FC, useMemo, useState } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { QUERY_ACTION_MAP, QUERY_MODAL_CONFIG } from './constants';
import { useQueryCapabilities } from './flow/useQueryCapabilities';
import { QueryEvent, QueryItem } from '@restore/queries/queries.types';
import { UserRoles } from '@dynamika-shared/types/auth';
import { QueryActionModal } from './QueryActionModal';
import Chevron from '@dynamika-shared/ui/icons/Chevron';

type QueryDropdownProps = {
  query: QueryItem;
  selectedRole: UserRoles;
  onSubmit: (event: QueryEvent, query: QueryItem, data: {
    username: string | null | undefined;
    password: string | null | undefined;
    signAgreement: boolean | null | undefined;
    message: string;
    attachments: File[];
  }) => void;
  updating: boolean;
};

export const QueryDropdown: FC<QueryDropdownProps> = ({ query, selectedRole, onSubmit, updating }) => {
  const capabilities = useQueryCapabilities(query, selectedRole);
  const [currentAction, setCurrentAction] = useState<QueryEvent | null>(null);
  const handleSubmitData = (data: {
    username: string | null | undefined;
    password: string | null | undefined;
    signAgreement: boolean | null | undefined;
    message: string;
    attachments: File[];
  }) => {
    if (!currentAction) {
      setCurrentAction(null);
      return;
    }
    onSubmit(currentAction, query, data);
    setCurrentAction(null);
  };

  const items: MenuProps['items'] = useMemo(
    () =>
      capabilities.availableEvents
        .filter(
          cap =>
            !cap.internal &&
            QUERY_ACTION_MAP?.[cap.event as keyof typeof QUERY_ACTION_MAP]
        )
        .map(cap => {
          const config =
            QUERY_ACTION_MAP?.[cap.event as keyof typeof QUERY_ACTION_MAP];
          return {
            key: cap.event,
            label: (
              <div className='flex items-center gap-2'>
                <config.icon className='w-[18px] h-[18px]' />
                <span>{config.label}</span>
              </div>
            ),
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              setCurrentAction(cap.event);
            },
          };
        }),
    [capabilities.availableEvents]
  );
  const modalConfig = useMemo(() => currentAction ? QUERY_MODAL_CONFIG[currentAction] : null, [currentAction]);
  return (
    <>
      <div className='flex items-center justify-center grow'>
        {items.length > 0 ? (
        <Dropdown
          menu={{ items }}
          overlayClassName={'w-[20rem]'}
          trigger={['click']}
        >
          <div
            className='flex h-5 cursor-pointer items-center justify-center'
            onClick={e => e.stopPropagation()}
          >
            <div className='rounded-3xl p-1 hover:bg-waterLight'>
              <DotsIcon className='text-dodgerBlue' />
            </div>
          </div>
        </Dropdown>) : <Chevron dir='right' className='w-2 h-2' />}
      </div>
      {modalConfig && (
        <div onClick={(e) => e.stopPropagation()}>
          <QueryActionModal
            open={true}
            onClose={() => setCurrentAction(null)}
            title={modalConfig?.title}
            okText={modalConfig?.okText}
            textareaLabel={modalConfig?.textareaLabel}
            textareaPlaceholder={modalConfig?.textareaPlaceholder}
            messageRequiredMsg={modalConfig?.messageRequiredMsg}
            enableAttachments={modalConfig?.enableAttachments}
            subtitle={modalConfig?.subtitle}
            onSubmitData={handleSubmitData}
            updating={updating}
          />
        </div>
      )}
    </>
  );
};
